// app/javascript/pages/Admin/TermsVersions/Show.tsx
import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { AppShell } from '@/components/shared/AppShell'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { KatexRenderer } from '@/components/shared/KatexRenderer'
import {
  ArrowLeft,
  ShieldCheck,
  Edit2,
  CheckCircle2,
  ExternalLink,
  Users,
  Calendar,
  UserCheck,
  FileText,
  History,
  Laptop
} from 'lucide-react'

interface VersionDetail {
  id: number
  version_number: string
  title: string
  summary?: string
  content: string
  is_active: boolean
  activated_at?: string
  created_at: string
  created_by_name: string
  acceptances_count: number
}

interface AcceptanceLog {
  id: number
  user_id: number
  user_name: string
  user_email: string
  user_role: string
  ip_address?: string
  user_agent?: string
  accepted_at: string
}

interface Props {
  version: VersionDetail
  recent_acceptances: AcceptanceLog[]
}

export default function TermsVersionsShow({ version, recent_acceptances }: Props) {
  const [activeTab, setActiveTab] = useState<'content' | 'audit'>('content')
  const [activating, setActivating] = useState(false)

  const handleActivate = () => {
    if (version.is_active) return
    const confirmed = window.confirm(
      `Activate Terms Version ${version.version_number}?\n\nThis will become the active legal agreement. All users who have not yet accepted v${version.version_number} will be presented with the mandatory terms acceptance modal on their next visit.`
    )
    if (!confirmed) return

    setActivating(true)
    router.post(
      `/admin/terms_versions/${version.id}/activate`,
      {},
      {
        onFinish: () => setActivating(false),
      }
    )
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-border">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm" className="h-9 w-9 p-0">
              <Link href="/admin/terms_versions">
                <ArrowLeft className="w-4 h-4" />
              </Link>
            </Button>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-2xl font-bold tracking-tight text-foreground">
                  {version.title}
                </h1>
                <span className="font-mono text-xs font-bold px-2.5 py-0.5 rounded-full bg-muted text-foreground border border-border">
                  v{version.version_number}
                </span>
                {version.is_active ? (
                  <Badge variant="default" className="gap-1 text-xs">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    <span>Currently Active & Enforced</span>
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">
                    Inactive Draft
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Created on {version.created_at} by {version.created_by_name}
                {version.activated_at && ` • Effective since ${version.activated_at}`}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <Button asChild variant="outline" size="sm" className="gap-1.5 text-xs">
              <a href={`/terms?version=${version.version_number}`} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-3.5 h-3.5" />
                <span>Public View</span>
              </a>
            </Button>

            <Button asChild variant="outline" size="sm" className="gap-1.5 text-xs">
              <Link href={`/admin/terms_versions/${version.id}/edit`}>
                <Edit2 className="w-3.5 h-3.5" />
                <span>Edit</span>
              </Link>
            </Button>

            {!version.is_active && (
              <Button
                size="sm"
                disabled={activating}
                onClick={handleActivate}
                className="gap-1.5 text-xs font-semibold"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>{activating ? 'Activating...' : 'Activate This Version'}</span>
              </Button>
            )}
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-border bg-card shadow-xs">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Total Acceptances
                </p>
                <p className="text-xl font-black text-foreground mt-1">
                  {version.acceptances_count}
                </p>
              </div>
              <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                <UserCheck className="w-4 h-4" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-xs">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Enforcement Status
                </p>
                <p className="text-sm font-bold text-foreground mt-1">
                  {version.is_active ? 'Active & Mandatory' : 'Inactive Draft'}
                </p>
              </div>
              <div className="w-9 h-9 rounded-xl bg-muted border border-border flex items-center justify-center text-muted-foreground">
                <ShieldCheck className="w-4 h-4" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-xs">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Effective Date
                </p>
                <p className="text-sm font-bold text-foreground mt-1">
                  {version.activated_at || 'Pending Activation'}
                </p>
              </div>
              <div className="w-9 h-9 rounded-xl bg-muted border border-border flex items-center justify-center text-muted-foreground">
                <Calendar className="w-4 h-4" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary Callout if available */}
        {version.summary && (
          <div className="p-4 rounded-xl border border-border bg-muted/40 text-xs space-y-1">
            <p className="font-bold uppercase tracking-wider text-foreground">Change Summary</p>
            <p className="text-muted-foreground leading-relaxed">{version.summary}</p>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 border-b border-border">
          <button
            type="button"
            onClick={() => setActiveTab('content')}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs sm:text-sm font-semibold border-b-2 transition-all cursor-pointer ${
              activeTab === 'content'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>Legal Text Content</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('audit')}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs sm:text-sm font-semibold border-b-2 transition-all cursor-pointer ${
              activeTab === 'audit'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <History className="w-4 h-4" />
            <span>Acceptance Audit Log ({recent_acceptances.length})</span>
          </button>
        </div>

        {/* Tab 1: Content Preview */}
        {activeTab === 'content' && (
          <Card className="border-border bg-card shadow-xs">
            <CardContent className="p-6 sm:p-8">
              <div className="text-foreground/90 leading-relaxed">
                <KatexRenderer
                  content={version.content}
                  format="markdown"
                  disableExamHelpers
                  className="markdown-content text-xs sm:text-sm leading-relaxed"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab 2: Audit Logs */}
        {activeTab === 'audit' && (
          <Card className="border-border bg-card shadow-xs">
            <CardHeader className="px-6 py-4 border-b border-border">
              <CardTitle className="text-base font-bold text-foreground">
                Recent User Acceptances
              </CardTitle>
              <CardDescription className="text-xs text-muted-foreground">
                Immutable legal proof including timestamp, IP address, and user agent header.
              </CardDescription>
            </CardHeader>

            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted/50 text-xs font-semibold uppercase text-muted-foreground border-b border-border">
                    <tr>
                      <th className="px-6 py-3">User</th>
                      <th className="px-6 py-3">Role</th>
                      <th className="px-6 py-3">Accepted At</th>
                      <th className="px-6 py-3">IP Address</th>
                      <th className="px-6 py-3">Device / User Agent</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {recent_acceptances.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">
                          No users have accepted this version yet.
                        </td>
                      </tr>
                    ) : (
                      recent_acceptances.map((log) => (
                        <tr key={log.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-6 py-3.5">
                            <div className="font-semibold text-foreground">{log.user_name}</div>
                            <div className="text-xs text-muted-foreground">{log.user_email}</div>
                          </td>
                          <td className="px-6 py-3.5">
                            <Badge variant="outline" className="text-[11px] capitalize">
                              {log.user_role}
                            </Badge>
                          </td>
                          <td className="px-6 py-3.5 text-xs font-mono text-muted-foreground">
                            {log.accepted_at}
                          </td>
                          <td className="px-6 py-3.5 text-xs font-mono text-muted-foreground">
                            {log.ip_address || '—'}
                          </td>
                          <td className="px-6 py-3.5 text-xs text-muted-foreground max-w-xs truncate" title={log.user_agent}>
                            {log.user_agent || '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppShell>
  )
}
